import { useState, useMemo, useCallback, useEffect, type ReactNode } from 'react';
import { ThemeContext, THEMES, DEFAULT_THEME } from './ThemeContext';

const DARK_MODE_STORAGE_KEY = 'bodywave-dark-mode';

/**
 * Get initial dark mode preference
 * Priority: localStorage > false (light mode default)
 * Note: System preference is NOT used - dark mode must be explicitly enabled
 */
const getInitialDarkMode = (): boolean => {
  // Check localStorage first
  const stored = localStorage.getItem(DARK_MODE_STORAGE_KEY);
  if (stored !== null) {
    return stored === 'true';
  }

  // Default to light mode (dark mode is opt-in only)
  return false;
};

/**
 * Apply theme and dark mode to document
 */
const applyTheme = (isDarkMode: boolean): void => {
  document.documentElement.setAttribute('data-theme', DEFAULT_THEME);
  document.documentElement.setAttribute('data-dark', isDarkMode ? 'true' : 'false');

  // Also set class for Tailwind dark mode compatibility
  if (isDarkMode) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
};

export interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Theme Provider Component
 * Provides VideoGame theme with light/dark mode support
 * Both modes are WCAG 2.2 AA compliant for color contrast
 */
export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [isDarkMode, setIsDarkModeState] = useState<boolean>(getInitialDarkMode);

  // Apply theme on mount and when dark mode changes
  useEffect(() => {
    applyTheme(isDarkMode);
  }, [isDarkMode]);

  // Listen for system preference changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      // Only auto-switch if user hasn't set a preference
      const stored = localStorage.getItem(DARK_MODE_STORAGE_KEY);
      if (stored === null) {
        setIsDarkModeState(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Toggle dark mode
  const toggleDarkMode = useCallback(() => {
    setIsDarkModeState((prev) => {
      const newValue = !prev;
      localStorage.setItem(DARK_MODE_STORAGE_KEY, String(newValue));
      return newValue;
    });
  }, []);

  // Set dark mode explicitly
  const setDarkMode = useCallback((isDark: boolean) => {
    localStorage.setItem(DARK_MODE_STORAGE_KEY, String(isDark));
    setIsDarkModeState(isDark);
  }, []);

  // Context value
  const contextValue = useMemo(
    () => ({
      theme: DEFAULT_THEME,
      isDarkMode,
      toggleDarkMode,
      setDarkMode,
      setTheme: () => {
        // No-op - theme is always videogame
        console.log('Theme switching is disabled. Using videogame theme.');
      },
      themes: THEMES,
      currentThemeMetadata: THEMES[0], // videogame is the only theme
    }),
    [isDarkMode, toggleDarkMode, setDarkMode]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};
